/*
 * PDP "Open lightbox" zoom: a self-contained fullscreen media carousel with
 * click / double-tap / pinch zoom. Isolated — no Swiper, no shared selectors.
 */
if (!customElements.get('pdp-zoom-lightbox')) {
  customElements.define(
    'pdp-zoom-lightbox',
    class PdpZoomLightbox extends HTMLElement {
      constructor() {
        super();

        // Tunables
        this.ZOOM_STEP = 2.5; // scale applied on click / double-tap zoom-in
        this.MAX_SCALE = 4; // hard cap for pinch zoom
        this.SWIPE_THRESHOLD = 50; // px to trigger a slide change on swipe
        this.DRAG_THRESHOLD = 15; // px before a pointerdown counts as a drag (vs click)
        this.LOOP = this.dataset.loop !== 'false';

        // State
        this.index = 0; // active real-slide index (0..count-1)
        this.position = 0; // track position including clones (real index + 1)
        this.scale = 1;
        this.tx = 0;
        this.ty = 0;
        this.isOpen = false;
        this.isAnimating = false;
        this.openedBy = null;
        this._gapPx = 0;

        // Bound handlers (so add/removeEventListener match)
        this._onKeydown = this._onKeydown.bind(this);
      }

      connectedCallback() {
        // Move to <body> once so no ancestor transform/overflow can clip the
        // fixed-position overlay (mirrors the theme's ModalDialog behaviour).
        if (!this._moved) {
          this._moved = true;
          document.body.appendChild(this);
          return; // re-enters connectedCallback after re-attach
        }
        if (this._initialised) return;
        this._initialised = true;

        this.viewport = this.querySelector('[data-pdpz-viewport]');
        this.track = this.querySelector('[data-pdpz-track]');
        this.currentEl = this.querySelector('[data-pdpz-current]');
        this.totalEl = this.querySelector('[data-pdpz-total]');

        // Collect the real slides (before clones are added)
        this.realSlides = Array.from(this.querySelectorAll('[data-pdpz-slide]'));
        this.count = this.realSlides.length;
        if (this.totalEl) this.totalEl.textContent = this.count;
        if (!this.count) return;

        this._buildClones();
        this.slides = Array.from(this.track.querySelectorAll('[data-pdpz-slide]'));

        this._bindControls();
        this._bindPointer();
      }

      /* ----------------------------- Loop clones ---------------------------- */

      _buildClones() {
        if (!this.LOOP || this.count < 2) {
          this.hasClones = false;
          return;
        }
        const first = this.realSlides[0];
        const last = this.realSlides[this.count - 1];
        const headClone = this._makeClone(last); // sits before the first
        const tailClone = this._makeClone(first); // sits after the last
        this.track.insertBefore(headClone, first);
        this.track.appendChild(tailClone);
        this.hasClones = true;
      }

      // Clones are visual only. Keep video posters (paused, not playing) so the
      // loop wrap shows the poster instead of flashing an empty slide.
      _makeClone(slide) {
        const clone = slide.cloneNode(true);
        clone.setAttribute('data-pdpz-clone', '');
        clone.removeAttribute('data-media-id');
        clone.querySelectorAll('video').forEach((v) => {
          v.removeAttribute('autoplay');
          v.removeAttribute('loop');
          v.preload = 'none';
        });
        clone.querySelectorAll('iframe, model-viewer').forEach((el) => el.remove());
        return clone;
      }

      /* ------------------------------- Binding ------------------------------ */

      _bindControls() {
        this.querySelectorAll('[data-pdpz-close]').forEach((el) =>
          el.addEventListener('click', () => this.hide())
        );
        const prev = this.querySelector('[data-pdpz-prev]');
        const next = this.querySelector('[data-pdpz-next]');
        if (prev) prev.addEventListener('click', () => this.go(-1));
        if (next) next.addEventListener('click', () => this.go(1));
        const zoom = this.querySelector('[data-pdpz-zoom]');
        if (zoom) zoom.addEventListener('click', () => this.toggleZoom());
      }

      _bindPointer() {
        this.pointers = new Map();
        this.dragging = false;
        this.startX = 0;
        this.startY = 0;
        this.startTx = 0;
        this.startTy = 0;
        this.startScale = 1;
        this.startDist = 0;
        this.lastTap = 0;
        this.movedDist = 0;

        this.viewport.addEventListener('pointerdown', this._pdown.bind(this));
        this.viewport.addEventListener('pointermove', this._pmove.bind(this));
        this.viewport.addEventListener('pointerup', this._pup.bind(this));
        this.viewport.addEventListener('pointercancel', this._pup.bind(this));
        // Prevent the browser's own image drag / native gestures on the image.
        this.viewport.addEventListener('dragstart', (e) => e.preventDefault());
      }

      /* -------------------------------- Open -------------------------------- */

      // Mirrors ModalDialog.show(opener) so the existing <modal-opener> markup
      // can open this element with zero changes to global.js.
      show(opener) {
        this.openedBy = opener;
        const mediaId = opener && opener.getAttribute('data-media-id');
        let start = 0;
        if (mediaId) {
          const i = this.realSlides.findIndex(
            (s) => s.getAttribute('data-media-id') === mediaId
          );
          if (i > -1) start = i;
        }
        this.open(start);
      }

      open(startIndex) {
        this.isOpen = true;
        this.setAttribute('open', '');
        document.body.classList.add('overflow-hidden');
        document.addEventListener('keydown', this._onKeydown);
        this.index = startIndex || 0;
        this.position = this.hasClones ? this.index + 1 : this.index;
        this._gapPx = parseFloat(getComputedStyle(this.track).columnGap) || 0;
        this._resetZoom(false);
        this._setTrack(false);
        this._updateUI();
        this._playActive();
        const closeBtn = this.querySelector('[data-pdpz-close]');
        if (closeBtn) closeBtn.focus({ preventScroll: true });
      }

      hide() {
        this.isOpen = false;
        this.removeAttribute('open');
        document.body.classList.remove('overflow-hidden');
        document.removeEventListener('keydown', this._onKeydown);
        this._resetZoom(false);
        this._pauseAll();
        if (this.openedBy && typeof this.openedBy.focus === 'function') {
          this.openedBy.focus({ preventScroll: true });
        }
      }

      _onKeydown(e) {
        if (!this.isOpen) return;
        if (e.key === 'Escape') this.hide();
        else if (e.key === 'ArrowRight') this.go(1);
        else if (e.key === 'ArrowLeft') this.go(-1);
      }

      /* ----------------------------- Navigation ----------------------------- */

      go(dir) {
        if (this.isAnimating || this.count < 2) return;
        if (this.scale > 1) return; // can't change slide while zoomed
        this.position += dir;
        this.index = (this.index + dir + this.count) % this.count;
        this._setTrack(true);
        this.isAnimating = true;
        this._updateUI();

        let finished = false;
        const onEnd = () => {
          if (finished) return;
          finished = true;
          clearTimeout(this._animTimer);
          this.track.removeEventListener('transitionend', onEnd);
          this.isAnimating = false;
          // Snap from a clone back to the matching real slide without animating.
          if (this.hasClones) {
            if (this.position === 0) {
              this.position = this.count;
              this._setTrack(false);
            } else if (this.position === this.count + 1) {
              this.position = 1;
              this._setTrack(false);
            }
          }
          this._playActive();
        };
        this.track.addEventListener('transitionend', onEnd);
        // Fallback so the slider can't get stuck if transitionend never fires.
        this._animTimer = setTimeout(onEnd, 450);
      }

      _setTrack(animate, extra) {
        this.track.style.transition = animate ? 'transform .35s ease' : 'none';
        // Each step is one slide width (100%) PLUS the inter-slide gap, so the
        // active slide always lines up even though there's a gap between them.
        const gap = this._gapPx || 0;
        const base = `-${this.position} * (100% + ${gap}px)`;
        const value = extra ? `calc(${base} + ${extra}px)` : `calc(${base})`;
        this.track.style.transform = `translate3d(${value}, 0, 0)`;
        if (!animate) {
          // Force reflow so a subsequent animated move starts from here.
          void this.track.offsetWidth;
        }
      }

      _updateUI() {
        if (this.currentEl) this.currentEl.textContent = this.index + 1;
        this.classList.toggle('is-zoomable', this._activeIsZoomable());
        this.classList.toggle('is-zoomed', this.scale > 1);
      }

      get activeSlide() {
        return this.slides[this.position];
      }

      _activeIsZoomable() {
        const s = this.activeSlide;
        return !!s && s.getAttribute('data-type') === 'image';
      }

      get activeZoomEl() {
        const s = this.activeSlide;
        return s ? s.querySelector('[data-pdpz-zoomwrap]') : null;
      }

      /* ----------------------------- Media play ----------------------------- */

      // Only the active slide's video plays; everything else is paused.
      _playActive() {
        this._pauseAll();
        const video = this.activeSlide?.querySelector('video');
        if (video) {
          const p = video.play();
          if (p && p.catch) p.catch(() => {});
        }
      }

      _pauseAll() {
        this.querySelectorAll('video').forEach((v) => {
          try {
            v.pause();
          } catch (e) {}
        });
      }

      /* ------------------------------- Zoom --------------------------------- */

      toggleZoom() {
        if (!this._activeIsZoomable()) return;
        if (this.scale > 1) this._resetZoom(true);
        else this._zoomTo(this.ZOOM_STEP, window.innerWidth / 2, window.innerHeight / 2, true);
      }

      // Zoom to `scale`, keeping the (clientX, clientY) point under the cursor.
      _zoomTo(scale, clientX, clientY, animate) {
        const el = this.activeZoomEl;
        if (!el) return;
        scale = Math.max(1, Math.min(this.MAX_SCALE, scale));

        // Convert pointer into a vector from the viewport centre.
        const cx = window.innerWidth / 2;
        const cy = window.innerHeight / 2;
        const px = (clientX - cx - this.tx) / this.scale; // image-space offset from centre
        const py = (clientY - cy - this.ty) / this.scale;

        this.scale = scale;
        this.tx = clientX - cx - px * scale;
        this.ty = clientY - cy - py * scale;
        this._clampPan();
        this._applyZoom(el, animate);
        this._updateUI();
      }

      _resetZoom(animate) {
        this.scale = 1;
        this.tx = 0;
        this.ty = 0;
        const el = this.activeZoomEl;
        if (el) this._applyZoom(el, animate);
        this.classList.remove('is-zoomed');
      }

      _applyZoom(el, animate) {
        el.style.transition = animate ? 'transform .3s ease' : 'none';
        el.style.transform = `translate3d(${this.tx}px, ${this.ty}px, 0) scale(${this.scale})`;
      }

      _clampPan() {
        const el = this.activeZoomEl;
        if (!el) return;
        const img = el.querySelector('img');
        const ref = img || el;
        // Natural fitted size at scale 1.
        const rect = ref.getBoundingClientRect();
        const fittedW = rect.width / this.scale;
        const fittedH = rect.height / this.scale;
        const scaledW = fittedW * this.scale;
        const scaledH = fittedH * this.scale;
        const maxX = Math.max(0, (scaledW - window.innerWidth) / 2);
        const maxY = Math.max(0, (scaledH - window.innerHeight) / 2);
        this.tx = Math.max(-maxX, Math.min(maxX, this.tx));
        this.ty = Math.max(-maxY, Math.min(maxY, this.ty));
      }

      /* ------------------------------ Pointer ------------------------------- */

      // Re-baseline a single-finger drag from a given point (used on pointerdown
      // and when the second finger lifts after a pinch).
      _startSingle(x, y) {
        this.startX = x;
        this.startY = y;
        this.startTx = this.tx;
        this.startTy = this.ty;
        this.swipeOffset = 0;
        this.movedDist = 0;
        this.dragging = false;
      }

      _pdown(e) {
        if (e.pointerType === 'mouse' && e.button !== 0) return;
        this.viewport.setPointerCapture?.(e.pointerId);
        this.pointers.set(e.pointerId, { x: e.clientX, y: e.clientY });

        if (this.pointers.size === 2) {
          // Begin pinch. Mark the whole gesture as a pinch so the lingering
          // finger can't trigger a slide change or tap when it lifts.
          const pts = [...this.pointers.values()];
          this.startDist = this._dist(pts[0], pts[1]);
          this.startScale = this.scale;
          this.pinching = true;
          this.pinchHappened = true;
          this.dragging = false;
          // Cancel any in-progress swipe so the track snaps back to the slide.
          if (this.scale <= 1) {
            this.swipeOffset = 0;
            this._setTrack(true);
          }
          return;
        }

        this._startSingle(e.clientX, e.clientY);
      }

      _pmove(e) {
        // Hover affordance: zoom-in cursor only while the mouse is over the
        // image (not the see-through backdrop). No button pressed.
        if (e.pointerType === 'mouse' && this.pointers.size === 0) {
          this.classList.toggle('pdpz--over-media', this._pointOnMedia(e.clientX, e.clientY));
        }
        if (!this.pointers.has(e.pointerId)) return;
        this.pointers.set(e.pointerId, { x: e.clientX, y: e.clientY });

        // Pinch zoom (two pointers)
        if (this.pinching && this.pointers.size >= 2) {
          const pts = [...this.pointers.values()];
          const dist = this._dist(pts[0], pts[1]);
          const mid = { x: (pts[0].x + pts[1].x) / 2, y: (pts[0].y + pts[1].y) / 2 };
          if (this.startDist > 0) {
            const target = this.startScale * (dist / this.startDist);
            this._zoomTo(target, mid.x, mid.y, false);
          }
          return;
        }

        // Only single-finger drags from here on.
        if (this.pointers.size !== 1) return;

        const dx = e.clientX - this.startX;
        const dy = e.clientY - this.startY;
        this.movedDist = Math.hypot(dx, dy);
        if (!this.dragging && this.movedDist < this.DRAG_THRESHOLD) return;
        this.dragging = true;

        if (this.scale > 1) {
          // Pan the zoomed image.
          this.classList.add('is-grabbing');
          this.tx = this.startTx + dx;
          this.ty = this.startTy + dy;
          this._clampPan();
          this._applyZoom(this.activeZoomEl, false);
        } else if (!this.pinchHappened && !this.isAnimating && this.count > 1) {
          // Swipe between slides; follow the finger. Suppressed during a pinch
          // or while a slide animation is still running (prevents a stuck track).
          this.swipeOffset = dx;
          this._setTrack(false, dx);
        }
      }

      _pup(e) {
        this.pointers.delete(e.pointerId);
        this.viewport.releasePointerCapture?.(e.pointerId);
        this.classList.remove('is-grabbing');

        // Ending a pinch (a finger lifted while two were down).
        if (this.pinching) {
          this.pinching = false;
          this.startDist = 0;
          // Settle the zoom: snap fully out if we're essentially at 1x.
          if (this.scale <= 1.02) this._resetZoom(true);
          else {
            this._clampPan();
            this._applyZoom(this.activeZoomEl, true);
            this._updateUI();
          }
          if (this.pointers.size === 1) {
            // One finger remains — re-baseline it but keep the gesture flagged
            // as a pinch so it can pan (if still zoomed) yet never slide/tap.
            const [p] = [...this.pointers.values()];
            this._startSingle(p.x, p.y);
          } else {
            this.pinchHappened = false;
          }
          return;
        }

        // Wait until every finger is up before resolving the gesture.
        if (this.pointers.size > 0) return;

        // A gesture that included a pinch must not tap or change slide.
        if (this.pinchHappened) {
          this.pinchHappened = false;
          this.dragging = false;
          if (this.scale <= 1) this._setTrack(true);
          return;
        }

        // Tap (no meaningful drag)
        if (!this.dragging) {
          this._handleTap(e);
          return;
        }

        // Settle the swipe: advance if past threshold, else snap back. Always
        // settle so the track can never be left stuck between two slides.
        if (this.scale <= 1 && this.count > 1) {
          const past = Math.abs(this.swipeOffset) > this.SWIPE_THRESHOLD;
          if (past && !this.isAnimating) {
            this.go(this.swipeOffset < 0 ? 1 : -1);
          } else {
            this._setTrack(true);
          }
        }
        this.dragging = false;
      }

      // True when the point falls within the visible media box (image or
      // video), not the see-through backdrop around it.
      _pointOnMedia(x, y) {
        const s = this.activeSlide;
        if (!s) return false;
        const mediaEl = s.querySelector('.pdpz__img') || s.querySelector('.pdpz__media');
        if (!mediaEl) return false;
        const r = mediaEl.getBoundingClientRect();
        return x >= r.left && x <= r.right && y >= r.top && y <= r.bottom;
      }

      _handleTap(e) {
        // Outside the media (the see-through backdrop) closes; if zoomed, a tap
        // anywhere zooms back out instead.
        if (!this._pointOnMedia(e.clientX, e.clientY)) {
          if (this.scale > 1) this._resetZoom(true);
          else this.hide();
          return;
        }
        if (!this._activeIsZoomable()) return;

        const isTouch = e.pointerType !== 'mouse';
        const isDouble = Date.now() - this.lastTap < 300;
        this.lastTap = Date.now();

        // Mouse: single click toggles zoom. Touch: only a double-tap zooms.
        if (!isTouch || isDouble) {
          if (this.scale > 1) this._resetZoom(true);
          else this._zoomTo(this.ZOOM_STEP, e.clientX, e.clientY, true);
        }
      }

      _dist(a, b) {
        return Math.hypot(a.x - b.x, a.y - b.y);
      }
    }
  );
}
